/**
 *  book_sort.js
 *
 *  Created by Satoshi Ishizu
 *  Copyright tateoki Inc. All rights reserved.
 */
"use strict";

import * as util from "./util";
import * as constants from "./constants";

// 抑止タイムスタンプ（ポップアップを閉じた直後の再オープン防止）
let bookSortSuppressUntil = 0;

/**
 * クリック座標に該当する要素を検索
 */
class ClickTargetFinder {
  static findBooksElement(booksElements, clickX) {
    return Array.from(booksElements).find((books) => {
      const rect = books.getBoundingClientRect();
      return clickX >= rect.left && clickX <= rect.right;
    });
  }

  static findBookElement(bookInfoElements, clickX, clickY) {
    return Array.from(bookInfoElements).find((bookInfo) => {
      const rect = bookInfo.getBoundingClientRect();
      return clickX >= rect.left && clickX <= rect.right && clickY >= rect.top && clickY <= rect.bottom;
    });
  }
}

/**
 * 書籍データ抽出ユーティリティ
 */
class BookDataExtractor {
  /**
   * 出版年月日を取得
   */
  static getPublishYear(bookEl) {
    const datasetDateIssue = bookEl.dataset.dateIssue; // "YYYY-MM-DD" 形式
    if (!datasetDateIssue) return 0;
    const iso = datasetDateIssue.trim();
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }

  /**
   * 著者名を取得
   */
  static getAuthor(bookEl) {
    const datasetAuthor = bookEl.dataset.authorKana;
    if (!datasetAuthor) return "";
    return datasetAuthor.trim().toLowerCase();
  }

  /**
   * タイトルを取得
   */
  static getTitle(bookEl) {
    const datasetTitle = bookEl.dataset.title;
    if (!datasetTitle) return "";
    return datasetTitle.trim().toLowerCase();
  }

  /**
   * 元の並び順インデックスを保存
   */
  static preserveOriginalOrder(bookElements) {
    bookElements.forEach((book, index) => {
      if (!book.hasAttribute("data-original-index")) {
        book.setAttribute("data-original-index", index);
      }
    });
  }
}

/**
 * 書籍ソート処理
 */
class BookSorter {
  /**
   * ソート実行と DOM への適用
   */
  static sortAndApply(container, booksArray, comparator, animationFn) {
    // アニメーション実行
    if (typeof animationFn === "function") animationFn();
    setTimeout(() => {
      requestAnimationFrame(() => {
        // ソート実行
        const sorted = [...booksArray].sort(comparator);
        sorted.forEach((book) => container.appendChild(book));
      });
    }, constants.ANIMATION_DURATION.VIEW_SCROLL_ARROW);
  }

  /**
   * ソートメニュー項目の定義を取得
   */
  static getSortMenuItems(container, bookElements, moveAnimation) {
    //
    const alignment = util.isLandscape() ? constants.ELEMENT_POSITION.CENTER : constants.ELEMENT_POSITION.LEFT;
    // 移動先の親カテゴリ探索
    const targetParent = container.parentElement;
    const targetElement = targetParent.querySelector(":scope > .category_spine_link");
    const animationFn = () => moveAnimation(constants.MULTI_LAYER.CATEGORY, targetElement, alignment);

    return [
      {
        id: "year-asc",
        label: "出版年: 古い順",
        action: () => this.sortAndApply(container, bookElements, (a, b) => BookDataExtractor.getPublishYear(a) - BookDataExtractor.getPublishYear(b), animationFn),
      },
      {
        id: "year-desc",
        label: "出版年: 新しい順",
        action: () => this.sortAndApply(container, bookElements, (a, b) => BookDataExtractor.getPublishYear(b) - BookDataExtractor.getPublishYear(a), animationFn),
      },
      {
        id: "title-asc",
        label: "タイトル: 昇順",
        action: () => this.sortAndApply(container, bookElements, (a, b) => BookDataExtractor.getTitle(a).localeCompare(BookDataExtractor.getTitle(b)), animationFn),
      },
      {
        id: "title-desc",
        label: "タイトル: 降順",
        action: () => this.sortAndApply(container, bookElements, (a, b) => BookDataExtractor.getTitle(b).localeCompare(BookDataExtractor.getTitle(a)), animationFn),
      },
      {
        id: "author-asc",
        label: "著者名: 昇順",
        action: () => this.sortAndApply(container, bookElements, (a, b) => BookDataExtractor.getAuthor(a).localeCompare(BookDataExtractor.getAuthor(b)), animationFn),
      },
      {
        id: "author-desc",
        label: "著者名: 降順",
        action: () => this.sortAndApply(container, bookElements, (a, b) => BookDataExtractor.getAuthor(b).localeCompare(BookDataExtractor.getAuthor(a)), animationFn),
      },
      {
        id: "reset",
        label: "元の並びに戻す",
        action: () => this.sortAndApply(container, bookElements, (a, b) => parseInt(a.getAttribute("data-original-index"), 10) - parseInt(b.getAttribute("data-original-index"), 10), animationFn),
      },
    ];
  }
}

/**
 * 本棚情報ヘッダー抽出ユーティリティ
 */
class BookshelfHeaderExtractor {
  /**
   * 本棚情報を取得
   * @param {HTMLElement} booksElement - .books要素
   * @returns {string} ヘッダーテキスト（上位カテゴリ名 > カテゴリ名）
   */
  static extractHeader(booksElement) {
    const currentCategory = BookshelfHeaderExtractor.findCategorySpineLink(booksElement);
    const parentCategory = BookshelfHeaderExtractor.findParentCategorySpineLink(booksElement);

    if (!currentCategory) return "";

    const currentDisplayName = currentCategory.getAttribute("data-display-name") || "";

    if (parentCategory) {
      const parentDisplayName = parentCategory.getAttribute("data-display-name") || "";
      return `${parentDisplayName} > ${currentDisplayName}`;
    }

    return currentDisplayName;
  }

  /**
   * 同階層のcategory_spine_linkを探す
   * @param {HTMLElement} booksElement - .books要素
   * @returns {HTMLElement|null} category_spine_link要素
   */
  static findCategorySpineLink(booksElement) {
    const parent = booksElement.parentElement;
    if (!parent) return null;
    return parent.querySelector(":scope > .category_spine_link");
  }

  /**
   * 1つ上の階層のcategory_spine_linkを探す
   * @param {HTMLElement} booksElement - .books要素
   * @returns {HTMLElement|null} 上位のcategory_spine_link要素
   */
  static findParentCategorySpineLink(booksElement) {
    const grandParent = booksElement.parentElement?.parentElement;
    if (!grandParent) return null;
    return grandParent.querySelector(":scope > .category_spine_link");
  }
}

/**
 * ソートポップアップ UI
 */
class SortPopup {
  constructor(clickX, clickY, menuItems, headerText = "") {
    this.clickX = clickX;
    this.clickY = clickY;
    this.menuItems = menuItems;
    this.headerText = headerText;
    this.element = null;
    this.handlers = {
      outsideClick: null,
      keydown: null,
    };
  }

  /**
   * ポップアップを作成
   */
  create() {
    this.element = document.createElement("div");
    this.element.className = "book-sort-popup";

    this.element.style.left = `${this.clickX}px`;
    this.element.style.top = `${this.clickY}px`;

    // ヘッダーがある場合は追加
    if (this.headerText) {
      const header = this.createHeader();
      this.element.appendChild(header);
    }

    const menuList = this.createMenuList();
    this.element.appendChild(menuList);

    return this.element;
  }

  /**
   * ヘッダーを作成
   */
  createHeader() {
    const header = document.createElement("div");
    header.className = "book-sort-header";
    header.textContent = this.headerText;
    return header;
  }

  /**
   * メニューリストを作成
   */
  createMenuList() {
    const ul = document.createElement("ul");
    ul.className = "book-sort-menu";

    this.menuItems.forEach((item) => {
      const li = this.createMenuItem(item);
      ul.appendChild(li);
    });

    return ul;
  }

  /**
   * メニュー項目を作成
   */
  createMenuItem(item) {
    const li = document.createElement("li");
    li.className = "book-sort-menu-item";
    li.textContent = item.label;
    li.dataset.id = item.id;

    li.addEventListener("click", (e) => {
      e.stopPropagation();
      try {
        item.action();
      } finally {
        this.close();
      }
    });

    return li;
  }

  /**
   * ポップアップを表示
   */
  show() {
    document.body.appendChild(this.element);
    this.adjustPosition();
    this.attachEventListeners();
  }

  /**
   * 画面内に収まるように位置を調整
   */
  adjustPosition() {
    const rect = this.element.getBoundingClientRect();
    let left = this.clickX;
    let top = this.clickY;

    if (rect.right > window.innerWidth) {
      left = Math.max(constants.BOOK_SORT.POPUP_OFFSET, window.innerWidth - rect.width - constants.BOOK_SORT.POPUP_OFFSET);
    }

    if (rect.bottom > window.innerHeight) {
      top = Math.max(constants.BOOK_SORT.POPUP_OFFSET, window.innerHeight - rect.height - constants.BOOK_SORT.POPUP_OFFSET);
    }

    this.element.style.left = `${left}px`;
    this.element.style.top = `${top}px`;
  }

  /**
   * イベントリスナーをアタッチ
   */
  attachEventListeners() {
    this.handlers.outsideClick = (e) => {
      if (!this.element.contains(e.target)) {
        this.close();
      }
    };

    this.handlers.keydown = (e) => {
      if (e.key === "Escape") {
        this.close();
      }
    };

    document.addEventListener("click", this.handlers.outsideClick, {
      capture: true,
    });
    document.addEventListener("keydown", this.handlers.keydown);

    // ポップアップ内のクリックはバブリングを止める
    this.element.addEventListener("click", (e) => e.stopPropagation());
  }

  /**
   * ポップアップを閉じる
   */
  close() {
    if (!this.element) return;

    this.element.remove();
    document.removeEventListener("click", this.handlers.outsideClick, {
      capture: true,
    });
    document.removeEventListener("keydown", this.handlers.keydown);

    // 閉じた直後に同じクリックで再オープンしないよう短時間抑止
    bookSortSuppressUntil = Date.now() + constants.BOOK_SORT.SUPPRESS_DURATION;
  }
}

/**
 * 本のソートメニュー表示処理
 * @param {Event} event - クリックイベント
 * @param {HTMLElement} container - 本棚コンテナ要素
 * @param {Function} moveAnimation - アニメーション移動関数
 */
function handleBookSortMenuDisplay(event, container, moveAnimation) {
  // 再オープン抑止チェック
  if (Date.now() < bookSortSuppressUntil) return;

  const booksElements = container.querySelectorAll(".books");
  const clickX = event.clientX;
  const clickY = event.clientY;

  // クリック座標に収まる books 要素を探す
  const clickedBooks = ClickTargetFinder.findBooksElement(booksElements, clickX);
  if (!clickedBooks) return;

  // 書籍数チェック
  const bookInfoElements = clickedBooks.querySelectorAll(".book > .book-info");
  if (bookInfoElements.length <= constants.BOOK_SORT.MIN_BOOKS_FOR_SORT) return;

  // 書籍自体がクリックされた場合は表示しない（余白クリックのみ反応）
  const clickedBook = ClickTargetFinder.findBookElement(bookInfoElements, clickX, clickY);
  if (clickedBook) return;

  // 並び替え対象の .book 要素を取得
  const bookElements = Array.from(clickedBooks.querySelectorAll(":scope > .book"));

  // 元順序を保存（初回のみ）
  BookDataExtractor.preserveOriginalOrder(bookElements);

  // メニュー項目を取得
  const menuItems = BookSorter.getSortMenuItems(clickedBooks, bookElements, moveAnimation);

  // 本棚情報のヘッダーテキストを取得
  const headerText = BookshelfHeaderExtractor.extractHeader(clickedBooks);

  // ポップアップを表示
  const popup = new SortPopup(clickX, clickY, menuItems, headerText);
  popup.create();
  popup.show();
}

export { handleBookSortMenuDisplay };
